import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { useLoaderData, useSearchParams } from '@remix-run/react'
import { Filter } from 'lucide-react'
import { AlleleFilter } from '#app/components/AlleleFilter'
import { ImmunologicalBackgroundFilter } from '#app/components/ImmunologicalBackgroundFilter'
import { ListOfPmhcResults } from '#app/components/list-of-pmhc-results'
import { StructureTypeFilter } from '#app/components/StructureTypeFilter'
import { SearchBar } from '#app/components/search-bar'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion'
import { Button } from '#app/components/ui/button'
import { prisma } from '#app/utils/db.server'

export const meta: MetaFunction = () => [{ title: 'Crosstope' }]

export async function loader({ request }: LoaderFunctionArgs) {
	const params = new URL(request.url).searchParams
	const searchTerm = params.get('search') ?? ''
	const allele = params.getAll('allele')
	const structureType = params.getAll('structure-type')
	const immunologicalBackground = params.getAll('immunological-background')

	const searchTermInput = [
		{ sequence: { contains: searchTerm } },
		{ mhc_allele_id: { contains: searchTerm } },
		{ source_protein: { contains: searchTerm } },
		{ structure_type_id: { contains: searchTerm } },
		{ immunological_background_id: { contains: searchTerm } },
		{ source_organism: { contains: searchTerm } },
		{ reference: { contains: searchTerm } },
		{ epitope_id_by_iedb: { contains: searchTerm } },
	]
	const alleleInput = [
		{ mhc_allele_id: allele.length ? { in: allele } : undefined },
	]
	const structureTypeInput = [
		{
			structure_type_id: structureType.length
				? { in: structureType }
				: undefined,
		},
	]
	const immunologicalBackgroundInput = [
		{
			immunological_background_id: immunologicalBackground.length
				? { in: immunologicalBackground }
				: undefined,
		},
	]
	const alleleOptions = await prisma.mHCAllele.findMany({
		include: {
			_count: {
				select: {
					pmhcs: {
						where: {
							OR: [...searchTermInput],
							AND: [...structureTypeInput, ...immunologicalBackgroundInput],
						},
					},
				},
			},
		},
	})
	const structureTypeOptions = await prisma.structureType.findMany({
		include: {
			_count: {
				select: {
					pmhcs: {
						where: {
							OR: [...searchTermInput],
							AND: [...alleleInput, ...immunologicalBackgroundInput],
						},
					},
				},
			},
		},
	})
	const immunologicalBackgroundOptions =
		await prisma.immunologicalBackground.findMany({
			include: {
				_count: {
					select: {
						pmhcs: {
							where: {
								OR: [...searchTermInput],
								AND: [...alleleInput, ...structureTypeInput],
							},
						},
					},
				},
			},
		})

	const isDirty =
		allele.length > 0 ||
		structureType.length > 0 ||
		immunologicalBackground.length > 0 ||
		searchTerm.length > 0

	const results = isDirty
		? await prisma.pmhc.findMany({
				include: {
					image: { select: { id: true, altText: true } },
					pdb_file: { select: { id: true } },
				},
				where: {
					OR: [...searchTermInput],
					AND: [
						...alleleInput,
						...structureTypeInput,
						...immunologicalBackgroundInput,
					],
				},
			})
		: []

	return json({
		status: 'idle',
		results,
		alleleOptions,
		structureTypeOptions,
		immunologicalBackgroundOptions,
		isDirty,
	})
}

export default function Index() {
	const data = useLoaderData<typeof loader>()
	const [, setSearchParams] = useSearchParams()
	return (
		<main className="font-poppins container px-4 py-8 md:px-6">
			<div className="mx-auto mb-8 text-center">
				<h1 className="mb-2 text-3xl font-bold tracking-tight">Crosstope</h1>
				<p className="mx-auto max-w-2xl text-slate-600">
					Search through our database of genomic sequences with advanced
					filtering capabilities
				</p>
			</div>
			<SearchBar />
			<Accordion type="single" collapsible className="mb-8">
				<AccordionItem value="filters" className="rounded-lg border bg-white">
					<AccordionTrigger className="px-4 py-3">
						<div className="flex items-center gap-2">
							<Filter className="h-4 w-4 text-slate-500" />
							<span className="font-medium">Filters</span>
						</div>
					</AccordionTrigger>
					<AccordionContent className="px-4 pb-4">
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
							<div className="space-y-2">
								<label className="text-sm font-medium">MHC Allele</label>
								<AlleleFilter options={data.alleleOptions} />
							</div>

							<div className="space-y-2">
								<label className="text-sm font-medium">Structure Type</label>
								<StructureTypeFilter options={data.structureTypeOptions} />
							</div>

							<div className="space-y-2">
								<label className="text-sm font-medium">
									Immunological Background
								</label>
								<ImmunologicalBackgroundFilter
									options={data.immunologicalBackgroundOptions}
								/>
							</div>
						</div>

						<div className="mt-4 flex justify-end">
							<Button
								size="sm"
								className="bg-teal-600 text-white hover:bg-teal-700"
								onClick={() => {
									setSearchParams(searchParams => {
										searchParams.delete('allele')
										searchParams.delete('structure-type')
										searchParams.delete('immunological-background')
										return searchParams
									})
								}}
							>
								Clear Filters
							</Button>
						</div>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
			{data.isDirty ? (
				<ListOfPmhcResults data={data.results} />
			) : (
				<div className="flex flex-col gap-4 px-4 py-8 xl:grid-cols-2 xl:gap-24">
					<p className="text-center text-slate-500">
						Search for a peptide sequence or select filters to narrow down your
						results
					</p>
				</div>
			)}
		</main>
	)
}
